<template>
  <div>
    <b-row class="mb-3">
      <b-col>
        <h3 class="flex-sm-fill">
          General
        </h3>
      </b-col>
      <b-col
        cols="auto"
        class="mr-auto"
      >
        <b-button
          v-if="$can('create', 'WalkthroughItem')"
          variant="outline-secondary"
          class="mr-1"
          @click="() => handleNewItemAdded()"
        >
          Add Note
        </b-button>
      </b-col>
    </b-row>
    <b-row
      v-if="items.length === 0 && !newItemVisible"
      class="px-5"
    >
      <b-col>
        You haven't added any notes yet
      </b-col>
    </b-row>
    <edit-item
      v-for="item in items"
      :key="item.id"
      :value="item"
      @input="(updatedItem) => updateItem(updatedItem)"
      @on-destroy="() => destroyItem(item)"
      @on-photo-uploaded="(photo) => addItemPhoto(item.id, photo)"
      @on-photo-destroyed="(photo) => destroyItemPhoto(item.id, photo)"
    />

    <edit-item
      :value="newItem"
      :input-ref="newItemInputRef"
      @on-content-changed="(changedContent) => newItem.content = changedContent"
      @input="(updatedItem) => updateNewItem(updatedItem)"
      @on-destroy="resetNewItem"
      @on-photo-uploaded="(photo) => addNewItemPhoto(photo)"
      @on-photo-destroyed="(photo) => destroyNewItemPhoto(photo)"
    />
  </div>
</template>

<script>
import EditItem from '@/components/admin/walkthroughItems/EditItem.vue';

export default {
  name: 'ItemsManager',
  components: {
    EditItem,
  },
  props: {
    walkthroughId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      items: [],
      newItem: {},
    };
  },
  methods: {
    getWalkthroughItems() {
      return this.$walkthroughsAPI
        .getItems(this.walkthroughId, { paginate: false })
        .then(({ walkthroughItems }) => {
          this.items = walkthroughItems;
        });
    },
    /********************************
    *** Methods for editing items ***
    ********************************/
    updateItem(updatedItem) {
      const itemIndex = this.items.findIndex((item) => item.id === updatedItem.id);
      if (itemIndex !== -1) {
        this.items[itemIndex] = updatedItem;
      }
    },
    destroyItem(item) {
      const itemIndex = this.items.findIndex((currentItem) => currentItem.id === item.id);
      if (itemIndex !== -1) {
        this.items.splice(itemIndex, 1);
      }
    },
    addItemPhoto(itemId, photo) {
      const itemIndex = this.items.findIndex((item) => item.id === itemId);
      if (itemIndex !== -1) {
        this.items[itemIndex].photos.push(photo);
      }
    },
    destroyItemPhoto(itemId, photo) {
      const itemIndex = this.items.findIndex((item) => item.id === itemId);
      const photoIndex = this.items[itemIndex].photos.findIndex((currPhoto) => currPhoto.id === photo.id);
      if (photoIndex !== -1) {
        this.items[itemIndex].photos.splice(photoIndex, 1);
      }
    },
    /**************************************
    *** Methods for managing a new item ***
    **************************************/
    createNewItem() {
      return this.$walkthroughsAPI
      .createItem(this.walkthroughId, { content: 'No notes' })
      .then((walkthroughItem) => {
        this.newItem = {...walkthroughItem, content: '' };
      });
    },
    updateNewItem(updatedItem) {
      if (updatedItem.id === this.newItem.id) {
        this.newItem = updatedItem;
      }
    },
    destroyNewItem() {
      return this.$walkthroughsAPI.destroyItem(this.newItem.id).then(() => this.resetNewItem());
    },
    addNewItemPhoto(photo) {
      this.newItem.photos.push(photo);
    },
    destroyNewItemPhoto(photo) {
      const photoIndex = this.newItem.photos.findIndex((currPhoto) => currPhoto.id === photo.id);
      if (photoIndex !== -1) {
        this.newItem.photos.splice(photoIndex, 1);
      }
    },
    resetNewItem() {
      this.newItem = new Object();
    },
    /**************************************
    *** Misc methods for managing items ***
    **************************************/
    handleNewItemAdded() {
      if (!this.newItemVisible) {
        this.createNewItem();
      } else if (this.newItemEdited) {
        const oldNewItem = { ...this.newItem };
        this.createNewItem().then(() => this.items.push(oldNewItem));
      }
    },
  },
  mounted() {
    this.getWalkthroughItems();
  },
  destroyed() {
    // When the user is done with updating the items, remove the new note if it
    // wasn't actually edited (note added or photo uploaded).
    if (this.newItemVisible && !this.newItemEdited) {
      this.destroyNewItem();
    }
  },
  computed: {
    showEmptyState() {
      return this.items.length === 0 && !this.newItemVisible;
    },
    newItemVisible() {
      return Object.keys(this.newItem).length > 0;
    },
    newItemEdited() {
      return this.newItemVisible && (this.newItem.content.length > 0 || this.newItem.photos.length > 0);
    },
    newItemInputRef() {
      return `add-item-${this.newItem?.id}`;
    },
  },
};
</script>
